import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import swal from 'sweetalert';
import { useTranslation } from 'react-i18next';
import CustomLoader from '../../components/CustomLoader';
import Colors from '../../components/ui/Colors';
import { CONTENT_TOP_OFFSET } from '../../layout/layoutMetrics';
import FederationOnboardingWizard from './FederationOnboardingWizard';
import SchedulePicker from './SchedulePicker';
import {
  listConnections,
  getConnection,
  fetchNow,
  pauseSchedule,
  resumeSchedule,
  deleteConnection,
  updateSchedule,
  stateChipColor,
  runStatusColor,
  isBroken,
  parseRunOutcome,
} from './federationApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// Only ingest outcomes carry `tables[]`; error / health-check outcomes do not.
const ingestOutcome = (run) => {
  const outcome = parseRunOutcome(run.outcomeJson);
  return outcome && Array.isArray(outcome.tables) ? outcome : null;
};

// A connection can carry a schedule (edit / pause / resume) once it has been
// validated; before that there is nothing to schedule.
const SCHEDULABLE = ['VALIDATED', 'SCHEDULED', 'ACTIVE', 'DEGRADED'];

export default function FederationWorkspace(props) {
  const { loginUser } = props;
  const jwt = loginUser?.jwtToken;
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(true);
  const [connections, setConnections] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [wizardConnection, setWizardConnection] = React.useState(null);

  const [detail, setDetail] = React.useState(null);

  const [scheduleConn, setScheduleConn] = React.useState(null);
  const [scheduleResult, setScheduleResult] = React.useState(null);
  const [scheduleBusy, setScheduleBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!jwt) return;
    const list = await listConnections(jwt);
    if (list) setConnections(list);
    else setError(t('Federation.errors.loadFailed'));
  }, [jwt, t]);

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);
      await load();
      setLoading(false);
    };
    init();
  }, [load]);

  // ── Wizard ────────────────────────────────────────────────────────────────
  const openNewWizard = () => {
    setWizardConnection(null);
    setWizardOpen(true);
  };

  const openResume = async (conn) => {
    const detailed = await getConnection(conn.id, jwt);
    setWizardConnection(detailed || conn);
    setWizardOpen(true);
  };

  const closeWizard = async () => {
    setWizardOpen(false);
    setWizardConnection(null);
    await load();
  };

  // ── Detail ──────────────────────────────────────────────────────────────────
  const openDetail = async (conn) => {
    const detailed = await getConnection(conn.id, jwt);
    if (detailed) setDetail(detailed);
    else setError(t('Federation.errors.detailFailed'));
  };

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleFetchNow = async (conn) => {
    setBusyId(conn.id);
    const run = await fetchNow(conn.id, jwt);
    setBusyId(null);
    if (!run) {
      swal({ title: t('Federation.errors.title'), text: t('Federation.errors.fetchNowFailed'), icon: 'error' });
      return;
    }
    swal({
      title: t('Federation.fetchNow.startedTitle'),
      text: t('Federation.fetchNow.startedBody', { runId: run.runId, status: run.status }),
      icon: 'success',
    });
    await load();
  };

  const handlePauseResume = async (conn) => {
    setBusyId(conn.id);
    const result = conn.scheduleEnabled ? await pauseSchedule(conn.id, jwt) : await resumeSchedule(conn.id, jwt);
    setBusyId(null);
    if (!result) {
      swal({ title: t('Federation.errors.title'), text: t('Federation.errors.pauseResumeFailed'), icon: 'error' });
      return;
    }
    await load();
  };

  const handleDelete = async (conn) => {
    const confirm = await swal({
      title: t('Federation.delete.confirmTitle'),
      text: t('Federation.delete.confirmBody', { source: conn.source || conn.id }),
      icon: 'warning',
      buttons: [t('Federation.cancel'), t('Federation.delete.confirmCta')],
      dangerMode: true,
    });
    if (!confirm) return;
    setBusyId(conn.id);
    const ok = await deleteConnection(conn.id, jwt);
    setBusyId(null);
    if (!ok) {
      swal({ title: t('Federation.errors.title'), text: t('Federation.errors.deleteFailed'), icon: 'error' });
      return;
    }
    await load();
  };

  // ── Edit schedule ─────────────────────────────────────────────────────────────
  const openSchedule = async (conn) => {
    const detailed = await getConnection(conn.id, jwt);
    setScheduleResult(null);
    setScheduleConn(detailed || conn);
  };

  const handleSaveSchedule = async (spec) => {
    if (!scheduleConn) return;
    setScheduleBusy(true);
    const saved = await updateSchedule(scheduleConn.id, spec, jwt);
    setScheduleBusy(false);
    if (!saved) {
      swal({ title: t('Federation.errors.title'), text: t('Federation.errors.scheduleFailed'), icon: 'error' });
      return;
    }
    setScheduleResult(saved);
    await load();
  };

  const renderActions = (conn) => {
    const disabled = busyId === conn.id;
    const schedulable = SCHEDULABLE.includes(conn.onboardingState);
    return (
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
        <Tooltip title={t('Federation.actions.detail')}>
          <span>
            <IconButton size="small" onClick={() => openDetail(conn)}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {isBroken(conn) ? (
          <Tooltip title={t('Federation.actions.reonboard')}>
            <span>
              <IconButton size="small" color="error" disabled={disabled} onClick={() => openResume(conn)}>
                <BuildCircleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={t('Federation.actions.fetchNow')}>
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => handleFetchNow(conn)}>
                <BoltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {schedulable && (
          <Tooltip title={t('Federation.actions.editSchedule')}>
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => openSchedule(conn)}>
                <ScheduleIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {schedulable && conn.frequency && (
          <Tooltip title={conn.scheduleEnabled ? t('Federation.actions.pause') : t('Federation.actions.resume')}>
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => handlePauseResume(conn)}>
                {conn.scheduleEnabled ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={t('Federation.actions.delete')}>
          <span>
            <IconButton size="small" color="error" disabled={disabled} onClick={() => handleDelete(conn)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // Per-run notes from the ingest outcome: EMPTY (no supported data), rejected
  // datasets and layout warnings. Details live in outcomeJson (runbook §11).
  const renderRunNotes = (run) => {
    const outcome = ingestOutcome(run);
    if (!outcome) return '—';
    const rejected = (outcome.rejected || []).length;
    const warnings = (outcome.warnings || []).length;
    const empty = run.status === 'COMPLETED' && outcome.tables.length === 0;
    if (!empty && !rejected && !warnings) return '—';
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {empty && <Chip size="small" color="warning" label={t('Federation.lastRunStatus.EMPTY')} />}
        {rejected > 0 && <Chip size="small" variant="outlined" color="warning" label={t('Federation.detail.rejectedCount', { n: rejected })} />}
        {warnings > 0 && <Chip size="small" variant="outlined" label={t('Federation.detail.warningsCount', { n: warnings })} />}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CustomLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pt: CONTENT_TOP_OFFSET, px: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
      <Box sx={{ width: '100%', maxWidth: 1400 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {t('Federation.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('Federation.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('Federation.refresh')}>
              <IconButton onClick={load}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openNewWizard}
              sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
            >
              {t('Federation.newConnection')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {connections.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t('Federation.empty.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 2 }}>
              {t('Federation.empty.body')}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewWizard} sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}>
              {t('Federation.newConnection')}
            </Button>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.cloud')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.source')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.nextRun')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.lastRun')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.table.lastStatus')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('Federation.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((conn) => (
                  <TableRow key={conn.id} hover>
                    <TableCell>
                      <Chip size="small" color={stateChipColor(conn.onboardingState)} label={t(`Federation.state.${conn.onboardingState}`)} />
                    </TableCell>
                    <TableCell>{t(`Federation.cloud.${conn.cloud}`)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all', maxWidth: 320 }}>
                      {conn.source || `${conn.bucketOrContainer || ''}/${conn.prefix || ''}`}
                    </TableCell>
                    <TableCell>{conn.scheduleEnabled ? fmt(conn.nextRunAt) : t('Federation.paused')}</TableCell>
                    <TableCell>{fmt(conn.lastRunAt)}</TableCell>
                    <TableCell>
                      {conn.lastRunStatus ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={runStatusColor(conn.lastRunStatus)}
                          label={t(`Federation.lastRunStatus.${conn.lastRunStatus}`, { defaultValue: conn.lastRunStatus })}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">{renderActions(conn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* Onboarding wizard */}
      <Dialog open={wizardOpen} onClose={closeWizard} maxWidth="md" fullWidth>
        <DialogTitle>{wizardConnection ? t('Federation.wizard.resumeTitle') : t('Federation.wizard.newTitle')}</DialogTitle>
        <DialogContent dividers>
          <FederationOnboardingWizard
            jwt={jwt}
            connection={wizardConnection}
            onConnectionChanged={(conn) => {
              // Keep the list live as the wizard advances the state.
              setConnections((prev) => {
                const exists = prev.some((c) => c.id === conn.id);
                return exists ? prev.map((c) => (c.id === conn.id ? { ...c, ...conn } : c)) : [conn, ...prev];
              });
            }}
            onCompleted={closeWizard}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWizard}>{t('Federation.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit-schedule dialog */}
      <Dialog open={!!scheduleConn} onClose={() => setScheduleConn(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Federation.schedule.editTitle')}</DialogTitle>
        <DialogContent dividers>
          {scheduleConn && (
            <SchedulePicker
              initialSpec={scheduleConn.schedule}
              onSubmit={handleSaveSchedule}
              submitting={scheduleBusy}
              submitLabel={t('Federation.schedule.save')}
              serverPreview={scheduleResult?.nextRunPreview}
              serverNextRunAt={scheduleResult?.nextRunAt}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleConn(null)}>{t('Federation.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog with recent runs */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('Federation.detail.title')}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <Chip color={stateChipColor(detail.onboardingState)} label={t(`Federation.state.${detail.onboardingState}`)} />
                <Chip variant="outlined" label={t(`Federation.cloud.${detail.cloud}`)} />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {detail.source}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 2 }}>
                {detail.boundPrincipal && (
                  <Typography variant="caption"><b>{t('Federation.detail.boundPrincipal')}:</b> {detail.boundPrincipal}</Typography>
                )}
                {detail.readMode && (
                  <Typography variant="caption"><b>{t('Federation.detail.readMode')}:</b> {t(`Federation.readMode.${detail.readMode}`)}</Typography>
                )}
                {detail.audience && (
                  <Typography variant="caption"><b>{t('Federation.detail.audience')}:</b> {detail.audience}</Typography>
                )}
                {detail.lastOnboardingStep && (
                  <Typography variant="caption"><b>{t('Federation.detail.lastStep')}:</b> {detail.lastOnboardingStep}</Typography>
                )}
                {detail.snapshotMode && (
                  <Typography variant="caption"><b>{t('Federation.detail.snapshotMode')}:</b> {t(`Federation.snapshotMode.${detail.snapshotMode}`, { defaultValue: detail.snapshotMode })}</Typography>
                )}
                {typeof detail.csvHeader === 'boolean' && (
                  <Typography variant="caption"><b>{t('Federation.detail.csvHeader')}:</b> {detail.csvHeader ? t('Federation.yes') : t('Federation.no')}</Typography>
                )}
                {detail.lastRunStatus && (
                  <Typography variant="caption"><b>{t('Federation.detail.lastRunStatus')}:</b> {t(`Federation.lastRunStatus.${detail.lastRunStatus}`, { defaultValue: detail.lastRunStatus })} ({fmt(detail.lastRunAt)})</Typography>
                )}
                {detail.schedule?.nextRunPreview && (
                  <Typography variant="caption" sx={{ gridColumn: '1 / -1' }}><b>{t('Federation.detail.schedule')}:</b> {detail.schedule.nextRunPreview}</Typography>
                )}
              </Box>

              {isBroken(detail) && (
                <Alert severity="error" sx={{ mb: 2 }} action={
                  <Button color="inherit" size="small" onClick={() => { setDetail(null); openResume(detail); }}>
                    {t('Federation.actions.reonboard')}
                  </Button>
                }>
                  {t('Federation.detail.brokenNote')}
                </Alert>
              )}

              {detail.lastRunStatus === 'EMPTY' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {t('Federation.detail.emptyRunTitle')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {t('Federation.detail.emptyRunHelp')}
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('Federation.detail.recentRuns')}
              </Typography>
              {(!detail.recentRuns || detail.recentRuns.length === 0) ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('Federation.detail.noRuns')}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.trigger')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.status')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.rows')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.objects')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.started')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.finished')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{t('Federation.detail.notes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.recentRuns.map((run) => (
                        <TableRow key={run.id}>
                          <TableCell>{run.trigger}</TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" color={runStatusColor(run.status)} label={run.status} />
                          </TableCell>
                          <TableCell>{run.rowCount ?? '—'}</TableCell>
                          <TableCell>{run.objectCount ?? '—'}</TableCell>
                          <TableCell>{fmt(run.startedAt)}</TableCell>
                          <TableCell>{fmt(run.finishedAt)}</TableCell>
                          <TableCell>{renderRunNotes(run)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>{t('Federation.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
